/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Logout Button
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       22nd December 2020

*******************************************************************************************/

import React                            from 'react'
import ExitToAppIcon                    from '@mui/icons-material/ExitToApp';
import { 
    useUser, 
    useTranslation
}                                       from 'contexts';  
import { 
    IconButton, 
    Button, 
    RefreshIcon 
}                                       from 'components';
import { useBreakpointQueries }         from 'hooks';

/**
 * A logout button component that allows users to log out. 
 * It includes an icon and the ability to customize the button's content and behavior.
 * 
 * @component
 * @example
 * // Using the default button and icon
 * <Logout onClick={handleLogout}>Logout</Logout>
 * 
 * @param {React.ReactNode} children - The content to display inside the button (e.g., text or other elements).
 * @param {React.ElementType} [component=Button] - The component type to render as the button (e.g., `Button`, `div`, etc.).
 * @param {React.ElementType} [icon=ExitToAppIcon] - The icon to display inside the button (default is the exit icon).
 * @param {boolean} [force=false] - Whether to force the logout action without any confirmation.
 * @param {...object} props - Additional props to pass to the component (e.g., onClick, style, etc.).
 * 
 * @returns {JSX.Element} The rendered logout button component.
 */
export const Logout = ({
    children, 
    component   : Component     = Button, 
    icon        : Icon          = ExitToAppIcon, 
    force                       = false, 
    ...props
}) => {

    // Hook to handle translation
    const [t] = useTranslation();

    // Hook for user authentication and loading state
    const {
        isLoading, 
        isAuthenticated, 
        logout
    } = useUser();

    // Hook for responsive breakpoints
    const { smUp } = useBreakpointQueries();

    // Local state for controlling the loading indicator
    const [loading, setLoading] = React.useState(false);

    // Resets the loading state
    const resetLoading = React.useCallback(() => {
        setLoading(false);
    }, []);

    // Handles the button click event to trigger the logout action
    const handleClick = React.useCallback((e) => {
        setLoading(true);                       // Set loading state to true
        logout(e)                               // Attempt logout
            .catch(err => {
                console.error("Error ogging out", err)  // Attempt logout
                setTimeout(resetLoading,1000);          // Reset loading state on error
            })
            .finally(() => {
                //
            })                             
    }, [logout, resetLoading]);

    // Effect hook to reset loading state when the page loads
    React.useEffect(() => {
        window.addEventListener('load', resetLoading);
        return () => {
            window.removeEventListener('load', resetLoading);
        };
    }, [resetLoading]);

    // Effect hook to reset loading when user is logged out (when isAuthenticated is false)
    React.useEffect(() => {
        if (!isAuthenticated) {
            resetLoading()
        }
    }, [isAuthenticated, resetLoading]);

    // Memoized condition to decide whether to show the button
    const show = React.useMemo(() => force || isAuthenticated, [force, isAuthenticated]);

    // Memoized disabled condition based on loading state
    const disabled = React.useMemo(() => isLoading || loading, [isLoading, loading]);

    // Don't show the component if the show condition is false
    if (!show) return null;

    return (
        <React.Fragment>
            {/* Mobile view - Icon button for small screens */}
            {
                !smUp && (
                <IconButton disabled={disabled} onClick={handleClick} size="large">
                    <Icon />
                </IconButton>
            )}

            {/* Desktop view - Regular button with label for larger screens */}
            {
                smUp && (
                <Component
                    disabled={disabled}
                    startIcon={loading ? <RefreshIcon loading={loading} /> : <Icon />}
                    {...props}
                    onClick={handleClick}
                >
                    {children || t('common.logout')}
                </Component>
            )}
        </React.Fragment>
    );
};

export default Logout;